'use client';

import { cn } from '@/utils/tailwind-merge';
import { ChevronDown } from 'lucide-react';
import React, { ButtonHTMLAttributes } from 'react';
import Loader from './Loader';

/* eslint-disable */
enum ButtonVariant {
    Primary = 'primary',
    Secondary = 'secondary',
    Arrow = 'arrow',
    Expand = 'expand',
}
/* eslint-enable */

interface CustomButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'secondary' | 'arrow' | 'expand';
    children?: React.ReactElement;
    className?: string;
    textClasses?: string;
    title?: string;
    disabled?: boolean;
    submitting?: boolean;
    color?: 'white' | 'blue';
    isExpanded?: boolean;
    index?: number;
    isActive?: boolean;
}

const Button = ({
    children,
    variant = ButtonVariant.Primary,
    className,
    textClasses,
    type = 'button',
    title,
    disabled = false,
    submitting = false,
    color = 'blue',
    isExpanded = false,
    index,
    isActive = false,
    ...buttonProps
}: CustomButtonProps) => {
    const isPrimary = variant === ButtonVariant.Primary;
    const isSecondary = variant === ButtonVariant.Secondary;
    const isArrow = variant === ButtonVariant.Arrow;
    const isExpand = variant === ButtonVariant.Expand;

    const isBlue = color === 'blue';

    const buttonClasses = cn(
        'group border-none focus:outline-none flex justify-center items-center transition-all outline-none w-fit hover:opacity-80 ',
        (isPrimary || isExpand) && 'py-3',
        isSecondary && !index && 'bg-primary py-5 px-16',
        index && 'border-b-[1px] border-solid py-3 justify-start items-start w-full',
        disabled && 'cursor-default pointer-events-none',
        disabled && isSecondary && 'bg-gray-400 text-text-primary',
        isBlue && !isSecondary ? 'text-primary border-primary' : 'text-text-primary border-white',
        isBlue &&
            index &&
            'text-black hover:text-primary border-black transition-colors duration-300',
        isBlue && index && isActive && 'text-primary',
        className,
    );

    const textStyles = cn(
        'uppercase sm:text-h6 max-sm:text-h6 leading-none text-nowrap',
        index && 'max-lg:text-h4 lg:text-h3',
        textClasses,
    );

    return (
        <button {...buttonProps} className={buttonClasses} type={type} disabled={disabled}>
            {submitting ? (
                <Loader />
            ) : (
                <span
                    className={cn(
                        (isPrimary || isExpand) && 'flex items-center',
                        (isPrimary || isExpand) && !isBlue && 'from-white to-white',
                        'text-black text-nowrap flex items-center text-center',
                        isArrow &&
                            'bg-left-bottom bg-gradient-to-r from-white to-white bg-[length:0%_2px] pb-2 bg-no-repeat group-hover:bg-[length:100%_2px] transition-all duration-500 ease-out',
                    )}
                >
                    {index && (
                        <span className="font-helvetica-neue font-medium text-sm max-xs:text-[12px] mr-2 lg:mt-1 text-nowrap">
                            {index < 10 ? `[ 0${index} ]` : `[ ${index} ]`}
                        </span>
                    )}
                    {title && <span className={textStyles}>{title}</span>}
                    {children}
                    {isExpand && (
                        <ChevronDown
                            className={cn(
                                'ml-8 scale-125 transition-transform duration-300',
                                isExpand && isExpanded && 'rotate-180',
                            )}
                            height={48}
                        />
                    )}
                </span>
            )}
        </button>
    );
};

export default Button;
